/**
 * The workbench keymap (OPSFL-POLISH-1) — ONE pure table that is both the behaviour and the
 * documentation. The queue and the drawer resolve every keystroke through the functions below and
 * the Shortcuts reference renders the same table, so the printed chord can never drift from the
 * chord that actually fires.
 *
 * Ergonomics: the analyst's hand stays on the home row for the whole triage loop — j/k to scan,
 * Enter to open, a number key for the decision, n for the note, Enter-free Esc to leave. Every
 * shortcut is a single unmodified key, and every single-key shortcut is suppressed while a text
 * field has focus (see `isTypingTarget`) so typing a note can never dispose a case.
 */
import type { WorkItem, WorkItemContext } from '@/api/types';
import { signalActionIntent, type Intent } from './meta';

export type ShortcutScope = 'queue' | 'drawer' | 'global';

export interface Shortcut {
  /** Display form of the chord(s), e.g. ['j', '↓']. First entry is the canonical one. */
  keys: string[];
  scope: ShortcutScope;
  label: string;
  /** Why it exists / what it will not do — shown in the reference, never on the hint bar. */
  note?: string;
}

/** The published keymap. Order = the order the reference lists them. */
export const WORKBENCH_SHORTCUTS: readonly Shortcut[] = [
  { keys: ['j', '↓'], scope: 'queue', label: 'Next item' },
  { keys: ['k', '↑'], scope: 'queue', label: 'Previous item' },
  { keys: ['Enter'], scope: 'queue', label: 'Open the item' },
  { keys: ['a'], scope: 'queue', label: 'Assign to me' },
  {
    keys: ['1', '…', '9'],
    scope: 'drawer',
    label: 'Record the numbered disposition',
    note: 'The numbers match the buttons on screen, top-left to bottom-right. Approvals have none — they are decided in the flow’s maker-checker screen.',
  },
  {
    keys: ['e'],
    scope: 'drawer',
    label: 'Escalate',
    note: 'Only when this item type offers an ESCALATE outcome. A note is required by policy.',
  },
  { keys: ['n'], scope: 'drawer', label: 'Jump to the note field' },
  { keys: ['[', ']'], scope: 'drawer', label: 'Previous / next tab' },
  { keys: ['Esc'], scope: 'drawer', label: 'Close the item (or leave the note field)' },
  { keys: ['⌘K', 'Ctrl K'], scope: 'global', label: 'Command palette — go anywhere' },
  { keys: ['?'], scope: 'global', label: 'What this page is for' },
];

/** True when the event came from somewhere the operator is typing — single keys must stand down. */
export function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

// ---- queue -------------------------------------------------------------------------------------

export type QueueAction = 'next' | 'prev' | 'open' | 'assign' | null;

/** Resolve a queue keystroke. Returns null for anything the queue does not own. */
export function queueAction(key: string): QueueAction {
  switch (key) {
    case 'j':
    case 'J':
    case 'ArrowDown':
      return 'next';
    case 'k':
    case 'K':
    case 'ArrowUp':
      return 'prev';
    case 'Enter':
      return 'open';
    case 'a':
    case 'A':
      return 'assign';
    default:
      return null;
  }
}

// ---- drawer ------------------------------------------------------------------------------------

export type DrawerAction =
  | { kind: 'option'; index: number }
  | { kind: 'escalate' }
  | { kind: 'note' }
  | { kind: 'tab'; delta: -1 | 1 }
  | null;

/**
 * Resolve a drawer keystroke. `1`–`9` are ZERO-BASED here (key `1` → index 0) because they name
 * the button the operator is looking at, not an array slot.
 */
export function drawerAction(key: string): DrawerAction {
  if (key >= '1' && key <= '9') return { kind: 'option', index: Number(key) - 1 };
  switch (key) {
    case 'e':
    case 'E':
      return { kind: 'escalate' };
    case 'n':
    case 'N':
      return { kind: 'note' };
    case '[':
      return { kind: 'tab', delta: -1 };
    case ']':
      return { kind: 'tab', delta: 1 };
    default:
      return null;
  }
}

// ---- disposition options -----------------------------------------------------------------------

export interface DispositionOption {
  /** The token posted to /disposition — the server's vocabulary, never a prettified label. */
  token: string;
  /** What the button reads. */
  label: string;
  intent: Intent;
  /** LINK_ENTITY cannot fire without a target entity id. */
  needsLinkTarget?: boolean;
}

const SIGNAL_ACTIONS = ['CLEAR', 'ESCALATE', 'LINK_ENTITY'] as const;
const RFI_ACTIONS = ['RESPONSE_RECEIVED', 'EXPIRE', 'CANCEL'] as const;

function asOption(token: string): DispositionOption {
  return {
    token,
    label: token.replace(/_/g, ' '),
    intent: signalActionIntent(token),
    needsLinkTarget: token === 'LINK_ENTITY',
  };
}

/**
 * The ordered disposition options for an item — the SINGLE source for both the rendered buttons
 * and the number keys, so key `2` always fires the second button on screen.
 *
 * APPROVAL returns none on purpose: approvals are decided in the flow's maker-checker screen where
 * the server enforces proposer ≠ approver. A number key must never appear to approve something.
 */
export function dispositionOptions(
  item: Pick<WorkItem, 'type'>,
  context: Pick<WorkItemContext, 'humanTaskOptions'>,
): DispositionOption[] {
  switch (item.type) {
    case 'APPROVAL':
      return [];
    case 'SIGNAL_REVIEW':
      return SIGNAL_ACTIONS.map(asOption);
    case 'RFI_FOLLOWUP':
      return RFI_ACTIONS.map(asOption);
    default:
      return (context.humanTaskOptions ?? []).map(asOption);
  }
}

/** The option a bare `e` fires, or null when this item type has no escalation outcome. */
export function escalateOption(options: readonly DispositionOption[]): DispositionOption | null {
  return options.find((o) => o.token.toUpperCase().includes('ESCAL')) ?? null;
}

/** The keycap shown on a disposition button — only the first nine are reachable by number. */
export function optionKeycap(index: number): string | null {
  return index < 9 ? String(index + 1) : null;
}
